/**
 * WebP Creator Utility
 * Converts images to WebP format for faster display in the UI.
 * Original files are preserved for download functionality.
 */

const MAX_DIMENSION = 1920; // Full HD max resolution
const WEBP_QUALITY = 0.9; // 90% quality

/**
 * Creates a WebP version of an image file.
 * @param file - The original image file
 * @returns Promise resolving to base64 WebP string, or null if conversion fails
 */
export const createWebpVersion = async (file: File): Promise<string | null> => {
  // Check if file is an image
  if (!file.type.startsWith('image/')) {
    console.warn('WebP conversion skipped: File is not an image');
    return null;
  }

  // Skip if already WebP
  if (file.type === 'image/webp') {
    console.info('WebP conversion skipped: File is already WebP format');
    return null;
  }

  try {
    return await new Promise<string | null>((resolve) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        const img = new Image();

        img.onload = () => {
          try {
            // Calculate new dimensions while maintaining aspect ratio
            let width = img.width;
            let height = img.height;

            // Scale down if larger than max dimension
            if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
              if (width > height) {
                // Landscape
                height = Math.round(height * (MAX_DIMENSION / width));
                width = MAX_DIMENSION;
              } else {
                // Portrait or square
                width = Math.round(width * (MAX_DIMENSION / height));
                height = MAX_DIMENSION;
              }
            }

            // Create canvas and context
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;

            const ctx = canvas.getContext('2d', { alpha: false });
            if (!ctx) {
              console.error('WebP conversion failed: Could not get canvas context');
              resolve(null);
              return;
            }

            // Set image smoothing for better quality
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            // Draw the image
            ctx.drawImage(img, 0, 0, width, height);

            // Convert to WebP format
            const webpDataUrl = canvas.toDataURL('image/webp', WEBP_QUALITY);

            // Verify conversion was successful
            if (!webpDataUrl.startsWith('data:image/webp')) {
              console.warn('WebP conversion failed: Browser may not support WebP encoding');
              resolve(null);
              return;
            }

            resolve(webpDataUrl);
          } catch (error) {
            console.error('WebP conversion failed:', error);
            resolve(null);
          }
        };

        img.onerror = () => {
          console.error('WebP conversion failed: Could not load image');
          resolve(null);
        };

        // Set image source to FileReader result
        if (event.target?.result) {
          img.src = event.target.result as string;
        } else {
          console.error('WebP conversion failed: Could not read file');
          resolve(null);
        }
      };

      reader.onerror = () => {
        console.error('WebP conversion failed: FileReader error');
        resolve(null);
      };

      // Read file as data URL
      reader.readAsDataURL(file);
    });
  } catch (error) {
    console.error('WebP conversion failed with exception:', error);
    return null;
  }
};

/**
 * Checks if the browser supports WebP encoding
 * @returns Promise resolving to true if WebP encoding is supported
 */
export const isWebpSupported = async (): Promise<boolean> => {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const dataUrl = canvas.toDataURL('image/webp');
    return dataUrl.startsWith('data:image/webp');
  } catch {
    return false;
  }
};
